import React from 'react';

import { HomePageLayoutComponent } from 'design/homePageLayout';
import { HomeCardLayoutComponent } from 'design/homeCardLayout';
import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { PressControlComponent } from 'design/pressControl';
import { useHistory } from 'react-router';
import { routes } from 'utils/routes';
import { LinkControlComponent } from 'design/linkControl';
import { ORIGIN_SITE_URL } from 'utils/constants';
import { LogoComponent } from 'design/logo';
import { ThemeProviderComponent } from 'design/themeProvider';
import { AccountControls } from 'containers/AccountControls';
import { MarginComponent } from 'design/margin';
import { useUser } from 'hooks/useUser';
import { normalizeProfileLike } from 'utils/profileLikeUtils';
import { ReactComponent as GeolocationIcon } from 'svg/geolocation.svg';
import { ReactComponent as PhoneIcon } from 'svg/phone.svg';
import { ReactComponent as HandbagIcon } from 'svg/handbag.svg';
import { ReactComponent as PersonsIcon } from 'svg/persons.svg';
import { ReactComponent as MailIcon } from 'svg/mail.svg';
import { useAppState } from 'hooks/useAppState';
import { IconRowComponent } from 'design/iconRow/iconRowComponents';
import { useDemoStackURLProcess } from 'hooks/useDemoStackURLProcess';
import { useProdStackInfoProcess } from 'hooks/useProdStackInfoProcess';
import { ISOCountries } from 'utils/isoCountries';

export const HomePageComponent = () => {
  useDemoStackURLProcess();
  useProdStackInfoProcess();

  const {
    homePage: ownDictionary,
    shared: sharedDictionary,
  } = useIntlDictionary();

  const history = useHistory();

  const user = useUser();

  const { demoStackUrl, prodStackInfo } = useAppState();

  const handleDemoActionPress = React.useCallback(() => {
    if (demoStackUrl) {
      window.open(demoStackUrl, '_self');
    } else {
      history.push(routes.registrationDemo());
    }
  }, [demoStackUrl, history]);

  const handleProdActionPress = React.useCallback(() => {
    if (prodStackInfo?.originURL) {
      window.open(prodStackInfo.originURL, '_self');
    } else {
      history.push(routes.registrationProd());
    }
  }, [history, prodStackInfo]);

  const demoStackHostName = demoStackUrl
    ? new URL(demoStackUrl).hostname
    : null;

  const prodStackHostName = prodStackInfo?.originURL
    ? new URL(prodStackInfo.originURL).hostname
    : null;

  const fullName = user?.person
    ? normalizeProfileLike(user.person).toFullName()
    : '';

  return (
    <HomePageLayoutComponent
      logo={
        <LinkControlComponent to={ORIGIN_SITE_URL}>
          <TextComponent size="l" color="primary">
            <LogoComponent />
          </TextComponent>
        </LinkControlComponent>
      }
      accountControls={
        <ThemeProviderComponent mode="onLight">
          <AccountControls />
        </ThemeProviderComponent>
      }
    >
      <HomeCardLayoutComponent size="auto" theme="light">
        <TextComponent theme="h2">{ownDictionary.demoTitleText}</TextComponent>
        <MarginComponent top={28} />
        <TextComponent color="primary">
          {ownDictionary.demoDescriptionText()}
        </TextComponent>
        <MarginComponent top="auto" />
        <PressControlComponent
          theme={demoStackHostName ? 'accent' : 'primary'}
          type="button"
          onPress={handleDemoActionPress}
        >
          {demoStackHostName ? demoStackHostName : ownDictionary.demoActionText}
        </PressControlComponent>
      </HomeCardLayoutComponent>
      <HomeCardLayoutComponent size="auto" theme="dark">
        <ThemeProviderComponent mode="onDark">
          <TextComponent theme="h2" family="futura">
            {ownDictionary.prodTitleText}
          </TextComponent>
          <MarginComponent top={28} />
          <TextComponent>{ownDictionary.prodDescriptionText()}</TextComponent>
          <MarginComponent top="auto" />
          <PressControlComponent
            theme={prodStackHostName ? 'accent' : 'primary'}
            type="button"
            onPress={handleProdActionPress}
          >
            {prodStackHostName
              ? prodStackHostName
              : ownDictionary.prodActionText}
          </PressControlComponent>
        </ThemeProviderComponent>
      </HomeCardLayoutComponent>
      <HomeCardLayoutComponent
        size={!!user?.org ? 'auto' : 'mini'}
        theme="light"
      >
        {fullName && (
          <>
            <TextComponent theme="s1">{fullName}</TextComponent>
            <MarginComponent top={28} />
          </>
        )}
        {user?.profileInfo?.position && (
          <>
            <TextComponent>{user.profileInfo.position}</TextComponent>
            <MarginComponent top={24} />
          </>
        )}
        <TextComponent color="secondary" size="s" upperCase>
          {ownDictionary.profileInfoLabel}
        </TextComponent>
        {user && (
          <>
            <MarginComponent top={12} />
            <IconRowComponent
              icon={<MailIcon />}
              text={<TextComponent>{user.accountInfo.email}</TextComponent>}
            />
          </>
        )}
        {user?.person?.phone && (
          <>
            <MarginComponent top={12} />
            <IconRowComponent
              icon={<PhoneIcon />}
              text={<TextComponent>{user.person.phone}</TextComponent>}
            />
          </>
        )}
        {user?.org && (
          <>
            <MarginComponent top={32} />
            <TextComponent color="secondary" size="s" upperCase>
              {ownDictionary.orgInfoLabel}
            </TextComponent>
            <MarginComponent top={12} />
            <IconRowComponent
              icon={<HandbagIcon />}
              text={<TextComponent>{user.org.name}</TextComponent>}
            />
            {user.org.staffCount ? (
              <>
                <MarginComponent top={12} />
                <IconRowComponent
                  icon={<PersonsIcon />}
                  text={
                    <TextComponent>
                      {sharedDictionary.orgStaffCount(user.org.staffCount)}
                    </TextComponent>
                  }
                />
              </>
            ) : null}
            <MarginComponent top={12} />
            <IconRowComponent
              icon={<GeolocationIcon />}
              text={
                <TextComponent>
                  {ISOCountries.getName(user.org.location, 'ru')}
                </TextComponent>
              }
            />
          </>
        )}
      </HomeCardLayoutComponent>
    </HomePageLayoutComponent>
  );
};
